import { cn } from "@synthesis/shared-utils";

import { CROSSING_UTILITY_STRUCTURE_TYPE_OPTIONS, getSelectOptionLabel } from "../../../../constants";
import {
  getDictionaryOptionLabel,
  getRailwayShortName,
  type CreateApplicationDictionaries,
} from "../../../create-application-page/create-application-dictionaries.lib";
import { useApplicationDictionariesContext } from "../../application-dictionaries-context";
import type { CrossingObjectDataFormValues } from "../../../create-application-page/create-application-page.lib";
import type { ApplicationDetailField } from "../../application-detail-page.lib";
import { DetailSection } from "../detail-section";

type CrossingObjectDescriptionContentProps = {
  crossingObjectData: CrossingObjectDataFormValues | null;
  className?: string;
};

type CrossingObjectDescriptionDetailSection = {
  title: string;
  fields: ApplicationDetailField[];
};

const formatCrossingObjectDescriptionValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  if (typeof value === "boolean") {
    return value ? "Да" : "Нет";
  }

  return String(value);
};

const buildIntersectionSection = (
  intersection: CrossingObjectDataFormValues["communications"][number]["intersections"][number],
  intersectionIndex: number,
): CrossingObjectDescriptionDetailSection => ({
  title: `Пересечение №${intersectionIndex + 1}`,
  fields: [
    {
      label: "Станция / Перегон (если известно)",
      value: formatCrossingObjectDescriptionValue(intersection.stationSection),
    },
    {
      label: "Км, пикет и метр (если известно)",
      value: formatCrossingObjectDescriptionValue(intersection.junctionLocation),
    },
    {
      label: "Координаты (если известно)",
      value: formatCrossingObjectDescriptionValue(intersection.coordinates),
    },
    {
      label: "Комментарий к месту пересечения",
      value: formatCrossingObjectDescriptionValue(intersection.comment),
    },
  ],
});

const buildCommunicationSections = (
  crossingObjectData: CrossingObjectDataFormValues | null,
  dictionaries: CreateApplicationDictionaries,
): CrossingObjectDescriptionDetailSection[] =>
  (crossingObjectData?.communications ?? []).flatMap((communication, index) => [
    {
      title: `Инженерная коммуникация №${index + 1}`,
      fields: [
        {
          label: "Вид инженерной коммуникации",
          value: getSelectOptionLabel(CROSSING_UTILITY_STRUCTURE_TYPE_OPTIONS, communication.utilityStructureType),
        },
        {
          label: "Назначение коммуникации",
          value: formatCrossingObjectDescriptionValue(communication.utilityPurpose),
        },
        {
          label: "Вид планируемых работ",
          value: getDictionaryOptionLabel(dictionaries.plannedWorksTypes, communication.plannedWorkType),
        },
        {
          label: "Технология устройства пересечения",
          value: formatCrossingObjectDescriptionValue(communication.crossingTechnology),
        },
        {
          label: "Основные параметры коммуникации",
          value: formatCrossingObjectDescriptionValue(communication.technicalCharacteristics),
        },
      ],
    },
    ...(communication.intersections ?? []).map((intersection, intersectionIndex) => buildIntersectionSection(intersection, intersectionIndex)),
  ]);

const buildCrossingObjectDescriptionSections = (
  crossingObjectData: CrossingObjectDataFormValues | null,
  dictionaries: CreateApplicationDictionaries,
): CrossingObjectDescriptionDetailSection[] => [
  {
    title: "Общая информация об объекте",
    fields: [
      { label: "Наименование объекта", value: formatCrossingObjectDescriptionValue(crossingObjectData?.projectName) },
      {
        label: "Железная дорога (филиал РЖД)",
        value: getRailwayShortName(dictionaries.railways, crossingObjectData?.railway),
      },
    ],
  },
  ...buildCommunicationSections(crossingObjectData, dictionaries),
  {
    title: "Сроки реализации проекта",
    fields: [
      {
        label: "Планируемый срок начала работ",
        value: formatCrossingObjectDescriptionValue(crossingObjectData?.implementationStartDate),
      },
      {
        label: "Планируемый срок завершения работ",
        value: formatCrossingObjectDescriptionValue(crossingObjectData?.implementationEndDate),
      },
      {
        label: "Планируемый срок ввода в эксплуатацию",
        value: formatCrossingObjectDescriptionValue(crossingObjectData?.commissioningDate),
      },
    ],
  },
];

export const CrossingObjectDescriptionContent = ({ crossingObjectData, className }: CrossingObjectDescriptionContentProps) => {
  const { dictionaries } = useApplicationDictionariesContext();

  return (
    <div className={cn("flex flex-col gap-6", className)}>
      {buildCrossingObjectDescriptionSections(crossingObjectData, dictionaries).map((section, index) => (
        <DetailSection key={`${section.title}-${index}`} title={section.title} fields={section.fields} />
      ))}
    </div>
  );
};
